import { useMemo } from "react";
import { useTheme } from "@mui/material";
import type { FilterState } from "../components/FilterDrawer";
import type { DateRange } from "../components/DurationSelector/types";
import type { MetricsFilters } from "../api/types";
import { buildFilterParams } from "../../features/admin/dashboards/shared/utils";
import {
  useRegions,
  useDepartments,
  useAgeRanges,
  useShiftPatterns,
} from "../components/Filters/hooks";
import {
  MAX_COMPARISON_GROUPS,
  type ComparisonDimension,
} from "./useComparisonMode";
import { getComparisonGroupColor } from "../utils/comparisonSeries";

export interface ComparisonGroup {
  /** Stable key (the selected filter value). */
  key: string;
  /** Human-readable label (resolved region/department/age/shift name). */
  label: string;
  /** Group colour, consistent across all charts on the page. */
  color: string;
  /** Filter params narrowed to this single group, for the metrics endpoints. */
  filterParams: MetricsFilters;
}

export interface UseComparisonGroupsResult {
  /** One group in average mode; one per selected value in comparison mode. */
  groups: ComparisonGroup[];
  /** True when comparison is active and rendering >1 group. */
  isComparing: boolean;
  /** Number of selected values dropped because the cap was exceeded. */
  overflowCount: number;
}

/** Maps a comparison dimension to the FilterState key holding its selected values. */
const DIMENSION_FILTER_KEY: Record<ComparisonDimension, keyof FilterState> = {
  region: "region",
  department: "department",
  ageRange: "ageRange",
  shiftPattern: "shiftPattern",
};

/**
 * Resolves the groups to render. In average mode (or when not comparing) it
 * returns a single group using the unchanged filter params — byte-for-byte the
 * current behaviour. In comparison mode it returns one group per selected value
 * of the active dimension, each with the active dimension narrowed to that one
 * value and all other filters preserved.
 */
export const useComparisonGroups = (
  filters: FilterState,
  dateRange: DateRange,
  clientId: string | null | undefined,
  comparison: { enabled: boolean; dimension: ComparisonDimension | null }
): UseComparisonGroupsResult => {
  const theme = useTheme();

  // Option sources for label resolution (cached; cheap to call unconditionally).
  const regions = useRegions();
  const departments = useDepartments({ regionIds: filters.region });
  const ageRanges = useAgeRanges();
  const shiftPatterns = useShiftPatterns();

  return useMemo(() => {
    const baseParams = buildFilterParams(filters, dateRange, clientId);

    if (!comparison.enabled || !comparison.dimension) {
      return {
        groups: [
          { key: "all", label: "All", color: theme.palette.primary.main, filterParams: baseParams },
        ],
        isComparing: false,
        overflowCount: 0,
      };
    }

    const dimension = comparison.dimension;
    const filterKey = DIMENSION_FILTER_KEY[dimension];
    const selectedValues = filters[filterKey] ?? [];

    // Resolve value -> label for the active dimension.
    const labelFor = (value: string): string => {
      switch (dimension) {
        case "region":
          return regions.data?.data?.find((r) => r.id === value)?.name ?? value;
        case "department":
          return (
            departments.data?.data?.find((d) => d.id === value)?.name ?? value
          );
        case "ageRange":
          return (
            ageRanges.data?.data?.find((o) => o.value === value)?.label ?? value
          );
        case "shiftPattern":
          return (
            shiftPatterns.data?.data?.find((o) => o.value === value)?.label ??
            value
          );
        default:
          return value;
      }
    };

    const capped = selectedValues.slice(0, MAX_COMPARISON_GROUPS);
    const overflowCount = selectedValues.length - capped.length;

    const groups: ComparisonGroup[] = capped.map((value, index) => {
      // Narrow the active dimension to this single value; keep every other filter.
      const narrowedFilters: FilterState = {
        ...filters,
        [filterKey]: [value],
      };
      return {
        key: value,
        label: labelFor(value),
        color: getComparisonGroupColor(index, theme),
        filterParams: buildFilterParams(narrowedFilters, dateRange, clientId),
      };
    });

    return { groups, isComparing: groups.length > 1, overflowCount };
  }, [
    filters,
    dateRange,
    clientId,
    comparison.enabled,
    comparison.dimension,
    regions.data,
    departments.data,
    ageRanges.data,
    shiftPatterns.data,
    theme,
  ]);
};
